import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaImage, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { COMPLAINT_CATEGORIES } from "../constants/complaintCategories";

import api from '../utils/api';
import './ComplaintForm.css';

const MAX_IMAGES = 2;

const ComplaintForm = () => {

  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    address: ''
  });

  const [issue, setIssue] = useState("");

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // index of the preview currently shown full-size, or null when closed
  const [expandedIndex, setExpandedIndex] = useState(null);

  // revoke object URLs on unmount / whenever previews change, so we
  // don't leak memory from URL.createObjectURL
  useEffect(() => {
    return () => {
      imagePreviews.forEach(src => URL.revokeObjectURL(src));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

  };

  const handleImageChange = (e) => {

    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    const availableSlots = MAX_IMAGES - images.length;

    if (availableSlots <= 0) {
      toast.error(t('complaintForm.maxImages'));
      e.target.value = '';
      return;
    }

    if (files.length > availableSlots) {
      toast.error(t('complaintForm.maxImages'));
    }

    const filesToAdd = files.slice(0, availableSlots);

    const oversized = filesToAdd.filter(f => f.size > 5 * 1024 * 1024);

    if (oversized.length > 0) {
      toast.error(t('complaintForm.imageTooLarge') || 'Each image must be under 5MB');
    }

    const validFiles = filesToAdd.filter(f => f.size <= 5 * 1024 * 1024);

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    setImages(prev => [...prev, ...validFiles]);

    setImagePreviews(prev => [
      ...prev,
      ...validFiles.map(file => URL.createObjectURL(file))
    ]);

    // reset the input so selecting the same file again still fires onChange
    e.target.value = '';

  };

  const handleRemoveImage = (index) => {

    URL.revokeObjectURL(imagePreviews[index]);

    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));

    if (expandedIndex === index) {
      setExpandedIndex(null);
    }

  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!issue) {
      toast.error(t("complaintForm.selectSubject"));
      return;
    }

    setLoading(true);

    try {

      const payload = new FormData();

      payload.append('category', formData.category);
      payload.append('issue', issue);
      payload.append('title', formData.title);
      payload.append('description', formData.description);
      payload.append('address', formData.address);

      images.forEach(img => payload.append('images', img));

      await api.post('/complaints', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('complaintForm.success'));

      navigate('/dashboard');

    } catch (error) {

      toast.error(
        error.response?.data?.message || t('complaintForm.failed')
      );

    } finally {
      setLoading(false);
    }

  };

  const selectedCategory = COMPLAINT_CATEGORIES.find(
    c => c.value === formData.category
  );

  return (

    <div className="form-page">

      <div className="form-container">

        <button
          className="back-btn"
          onClick={() => navigate('/dashboard')}
        >
          <FaArrowLeft /> {t('common.backToDashboard')}
        </button>

        <h2>{t('complaintForm.heading')}</h2>

        <form onSubmit={handleSubmit}>

          {/* CATEGORY */}
          <div className="form-group">

            <label>{t('complaintForm.category')} *</label>

            <select
              name="category"
              className="form-control"
              value={formData.category}
              onChange={handleChange}
              required
            >

              <option value="">
                {t('complaintForm.selectCategory')}
              </option>

              {COMPLAINT_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {i18n.language === "mr" ? cat.label_mr : cat.label_en}
                </option>
              ))}

            </select>

          </div>

          {/* ISSUE */}
          <div className="form-group">

            <label>{t("complaintForm.subject")} *</label>

            <select
              className="form-control"
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              disabled={!formData.category}
              required
            >
              <option value="">
                {t("complaintForm.selectSubject")}
              </option>

              {selectedCategory?.issues?.map((item) => (
                <option key={item.code} value={item.code}>
                  {i18n.language === "mr" ? item.mr : item.en}
                </option>
              ))}
            </select>

          </div>

          {/* TITLE */}
          <div className="form-group">

            <label>{t('complaintForm.title')} *</label>

            <input
              type="text"
              name="title"
              className="form-control"
              placeholder={t('complaintForm.titlePlaceholder')}
              value={formData.title}
              onChange={handleChange}
              maxLength={200}
              required
            />

          </div>

          {/* DESCRIPTION */}
          <div className="form-group">

            <label>{t('complaintForm.description')} *</label>

            <textarea
              name="description"
              className="form-control"
              placeholder={t('complaintForm.descriptionPlaceholder')}
              value={formData.description}
              onChange={handleChange}
              maxLength={1000}
              required
            />

          </div>

          {/* ADDRESS */}
          <div className="form-group">

            <label>{t('complaintForm.address')}</label>

            <input
              type="text"
              name="address"
              className="form-control"
              placeholder={t('complaintForm.addressPlaceholder')}
              value={formData.address}
              onChange={handleChange}
            />

          </div>

          {/* IMAGE UPLOAD */}
          <div className="form-group">

            <label>{t('complaintForm.images')} (Max {MAX_IMAGES})</label>

            <div className="file-upload">

              <input
                type="file"
                id="images"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                disabled={images.length >= MAX_IMAGES}
                style={{ display: 'none' }}
              />

              <label
                htmlFor="images"
                className={`file-upload-label ${images.length >= MAX_IMAGES ? 'disabled' : ''}`}
              >
                <FaImage /> {t('complaintForm.chooseImages')}
              </label>

              <span className="file-count">
                {images.length > 0
                  ? `${images.length} ${t('complaintForm.imagesSelected')}`
                  : t('complaintForm.noImages')
                }
              </span>

            </div>

            {imagePreviews.length > 0 && (

              <div className="image-previews">

                {imagePreviews.map((src, i) => (
                  <div className="image-preview-item" key={i}>
                    <img
                      src={src}
                      alt={`preview-${i}`}
                      onClick={() => setExpandedIndex(i)}
                    />
                    <button
                      type="button"
                      className="image-remove-btn"
                      onClick={() => handleRemoveImage(i)}
                      aria-label="Remove image"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}

              </div>

            )}

          </div>

          {/* ACTION BUTTONS */}
          <div className="form-actions">

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              <FaArrowLeft /> {t('common.backToDashboard')}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              {t('common.cancel')}
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? t('common.submitting')
                : t('complaintForm.submit')
              }
            </button>

          </div>

        </form>

      </div>

      {/* LIGHTBOX */}
      {expandedIndex !== null && imagePreviews[expandedIndex] && (

        <div
          className="lightbox-overlay"
          onClick={() => setExpandedIndex(null)}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={() => setExpandedIndex(null)}
            aria-label="Close"
          >
            <FaTimes />
          </button>

          <img
            src={imagePreviews[expandedIndex]}
            alt={`preview-${expandedIndex}-expanded`}
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

      )}

    </div>

  );

};

export default ComplaintForm;